import { Chip, type ChipProps } from '@mui/material';
import { type FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CARRIER_LABELS } from './routerSimForm.js';
import { type RouterNetworkStatus } from '../../redux/reducers/routerNetwork.js';
import { getRouterNetworkForPatient } from '../../redux/selectors.js';

interface RouterNetworkBadgeProps {
  patientId: string;
}

interface BadgeContent {
  label: string;
  color: ChipProps['color'];
}

export function badgeContentFor(status: RouterNetworkStatus | undefined): BadgeContent | null {
  if (!status) {
    return null;
  }
  if (status.testing) {
    return { label: 'Network test in progress', color: 'info' };
  }
  if (status.state === 'switching') {
    return { label: 'Switching network…', color: 'info' };
  }
  if (status.state === 'offline') {
    return { label: 'Network offline', color: 'error' };
  }
  if (status.state === 'degraded') {
    return { label: 'Unstable network', color: 'warning' };
  }
  if (!status.onPrimary) {
    const carrier = status.carrier ? ` · ${CARRIER_LABELS[status.carrier]}` : '';
    return { label: `Backup SIM${carrier}`, color: 'info' };
  }
  return null;
}

const RouterNetworkBadge: FC<RouterNetworkBadgeProps> = ({ patientId }) => {
  const status = useSelector(useMemo(() => getRouterNetworkForPatient(patientId), [patientId]));
  const content = badgeContentFor(status);
  if (!content) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={content.label}
      color={content.color}
      sx={{ position: 'absolute', top: 8, left: 8, zIndex: 8, pointerEvents: 'none' }}
    />
  );
};

export default RouterNetworkBadge;
